import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { ACTIVITY_LEVELS, GOAL_LABELS, TRAINING_EXPERIENCE_LABELS, TRAINING_FREQUENCY_LABELS } from '../utils/dynamics';
import { Copy, Check, MessageCircle } from 'lucide-react';

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form,    setForm]    = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');
  const [copied,  setCopied]  = useState(false);
  const [token,   setToken]   = useState('');

  const [pwdForm,  setPwdForm]  = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg,    setPwdMsg]    = useState('');

  useEffect(() => {
    api.get('/users/profile').then(r => {
      const u = r.data.user;
      setForm({
        first_name:          u.first_name          || '',
        last_name:           u.last_name           || '',
        phone:               u.phone               || '',
        telegram_username:   u.telegram_username   || '',
        birth_date:          u.birth_date          ? u.birth_date.slice(0, 10) : '',
        gender:              u.gender              || '',
        height_cm:           u.height_cm           || '',
        goal:                u.goal                || '',
        activity_level:      u.activity_level ? String(parseFloat(u.activity_level)) : '',
        training_experience: u.training_experience || '',
        training_frequency:  u.training_frequency  || '',
      });
      setToken(u.telegram_token || '');
    }).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.gender) { setError('Укажи пол — он нужен для расчёта % жира и КБЖУ'); return; }
    setSaving(true);
    try {
      const { data } = await api.put('/users/profile', form);
      setUser(prev => ({ ...prev, ...data.user }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwdMsg('');
    if (!pwdForm.currentPassword || !pwdForm.newPassword) {
      setPwdMsg('Заполните оба поля'); return;
    }
    if (pwdForm.newPassword.length < 6) {
      setPwdMsg('Новый пароль — минимум 6 символов'); return;
    }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) {
      setPwdMsg('Пароли не совпадают'); return;
    }
    setPwdSaving(true);
    try {
      await api.put('/users/password', {
        currentPassword: pwdForm.currentPassword,
        newPassword: pwdForm.newPassword,
      });
      setPwdMsg('✅ Пароль изменён');
      setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPwdMsg(''), 3000);
    } catch (err) {
      setPwdMsg('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setPwdSaving(false);
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="w-8 h-8 rounded-full border-2 border-fuchsia border-t-transparent animate-spin" />
    </div>
  );

  const selectedActivity = ACTIVITY_LEVELS.find(a => String(a.value) === String(form.activity_level));

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <h1 className="page-title">Профиль</h1>

      {error && (
        <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 px-4 py-3 rounded-xl">{error}</p>
      )}

      <form onSubmit={handleSave} className="space-y-6">

        {/* Личные данные */}
        <div>
          <p className="section-label">Личные данные</p>
          <div className="card p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Имя</label>
                <input className="input" value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="Алёна" />
              </div>
              <div>
                <label className="input-label">Фамилия</label>
                <input className="input" value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Молчанова" />
              </div>
            </div>
            <div>
              <label className="input-label">Номер телефона</label>
              <input className="input" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+7 900 000 00 00" />
            </div>
            <div>
              <label className="input-label">Telegram username</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-light-muted dark:text-ink-muted text-sm">@</span>
                <input className="input pl-7" value={form.telegram_username} onChange={e => set('telegram_username', e.target.value.replace('@',''))} placeholder="username" autoCapitalize="off" />
              </div>
              <p className="text-xs text-ink-light-muted dark:text-ink-muted mt-1">Тренер сможет написать тебе напрямую в Telegram</p>
            </div>
          </div>
        </div>

        {/* Анкета */}
        <div>
          <p className="section-label">Анкета</p>
          <div className="card p-4 space-y-4">

            {/* Пол — важный блок, крупно */}
            <div>
              <label className="input-label">
                Пол <span className="text-fuchsia">*</span>
                <span className="text-ink-light-muted dark:text-ink-muted font-normal ml-1">(нужен для расчёта % жира и КБЖУ)</span>
              </label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                {[
                  { value: 'female', label: '👩 Женский' },
                  { value: 'male',   label: '👨 Мужской' },
                ].map(g => (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => set('gender', g.value)}
                    className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                      form.gender === g.value
                        ? 'bg-fuchsia text-white border-fuchsia'
                        : 'border-surface-light-border dark:border-surface-600 text-ink-light dark:text-ink-dark hover:border-fuchsia/50'
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Дата рождения</label>
                <input className="input" type="date" value={form.birth_date} onChange={e => set('birth_date', e.target.value)} />
              </div>
              <div>
                <label className="input-label">Рост (см)</label>
                <input className="input" type="number" step="0.5" placeholder="168" value={form.height_cm} onChange={e => set('height_cm', e.target.value)} />
              </div>
            </div>

            <div>
              <label className="input-label">Цель</label>
              <select className="input" value={form.goal} onChange={e => set('goal', e.target.value)}>
                <option value="">Выбери...</option>
                {Object.entries(GOAL_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="input-label">Уровень активности</label>
              <select className="input" value={form.activity_level} onChange={e => set('activity_level', e.target.value)}>
                <option value="">Выбери...</option>
                {ACTIVITY_LEVELS.map(al => (
                  <option key={al.value} value={al.value}>{al.label}</option>
                ))}
              </select>
              {selectedActivity && (
                <div className="mt-2 px-3 py-2.5 rounded-xl bg-fuchsia/5 dark:bg-fuchsia/10 border border-fuchsia/20 text-xs text-ink-light dark:text-ink-dark leading-relaxed">
                  {selectedActivity.desc}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Стаж тренировок</label>
                <select className="input" value={form.training_experience} onChange={e => set('training_experience', e.target.value)}>
                  <option value="">Выбери...</option>
                  {Object.entries(TRAINING_EXPERIENCE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="input-label">Частота тренировок</label>
                <select className="input" value={form.training_frequency} onChange={e => set('training_frequency', e.target.value)}>
                  <option value="">Выбери...</option>
                  {Object.entries(TRAINING_FREQUENCY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>

          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary w-full">
          {saving ? 'Сохраняю...' : saved ? '✓ Сохранено' : 'Сохранить профиль'}
        </button>
      </form>

      {/* Безопасность */}
      <div>
        <p className="section-label">Безопасность</p>
        <form onSubmit={handlePasswordChange} className="card p-4 space-y-4">
          <div>
            <label className="input-label">Текущий пароль</label>
            <input className="input" type="password" autoComplete="current-password"
              value={pwdForm.currentPassword}
              onChange={e => setPwdForm(f => ({ ...f, currentPassword: e.target.value }))} />
          </div>
          <div>
            <label className="input-label">Новый пароль (мин. 6 символов)</label>
            <input className="input" type="password" autoComplete="new-password"
              value={pwdForm.newPassword}
              onChange={e => setPwdForm(f => ({ ...f, newPassword: e.target.value }))} />
          </div>
          <div>
            <label className="input-label">Повторите новый пароль</label>
            <input className="input" type="password" autoComplete="new-password"
              value={pwdForm.confirmPassword}
              onChange={e => setPwdForm(f => ({ ...f, confirmPassword: e.target.value }))} />
          </div>
          {pwdMsg && (
            <p className="text-xs" style={{ color: pwdMsg.startsWith('✅') ? '#22C55E' : '#EF4444' }}>{pwdMsg}</p>
          )}
          <button type="submit" disabled={pwdSaving} className="btn-primary w-full">
            {pwdSaving ? 'Сохраняю...' : 'Сменить пароль'}
          </button>
          <p className="text-xs text-ink-light-muted dark:text-ink-muted">
            Если Telegram привязан, мы пришлём уведомление о смене пароля — если это были не вы, сразу напишите тренеру.
          </p>
        </form>
      </div>

      {/* Telegram */}
      <div>
        <p className="section-label">Telegram</p>
        <div className="card p-4 space-y-3">
          <p className="text-sm text-ink-light dark:text-ink-dark">
            Привяжи Telegram — потом можно входить на сайт без пароля, одним нажатием.
          </p>
          {token && (
            <a
              href={`https://t.me/MolShape_bot?start=${token}`}
              target="_blank"
              rel="noreferrer"
              className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
            >
              <MessageCircle size={16} />
              Подключить Telegram — одним нажатием
            </a>
          )}
          <p className="text-xs text-ink-light-muted dark:text-ink-muted">
            Нажми кнопку → Telegram откроется → бот автоматически привяжет твой аккаунт.
          </p>
        </div>
      </div>
    </div>
  );
}
